/**
 * Copyright (c) Streamlit Inc. (2018-2022) Snowflake Inc. (2022-2026)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { memo, type ReactElement } from "react"

import type { Text as TextProto } from "@streamlit/protobuf"

import { StyledLabelHelpWrapper } from "~lib/components/shared/TooltipIcon/styled-components"
import { InlineTooltipIcon } from "~lib/components/shared/TooltipIcon/TooltipIcon"
import { useLabelTitleTooltip } from "~lib/hooks/useLabelTitleTooltip"

import {
  StyledInlineHelpIcon,
  StyledText,
  StyledTextBody,
} from "./styled-components"

export interface TextProps {
  element: TextProto
}

/**
 * Replace newlines with spaces so wrap=False stays on one line.
 *
 * wrap=False uses nowrap + white-space-collapse:preserve so extra spaces
 * stay visible (GH#10062). That combination computes to white-space:pre,
 * which would otherwise honor newlines as extra rows.
 */
function collapseNewlines(body: string): string {
  return body.replaceAll(/\r\n|\r|\n/g, " ")
}

/**
 * Functional element representing preformatted (plain) text.
 */
function TextElement({ element }: Readonly<TextProps>): ReactElement {
  const truncate = element.wrap === false
  const { titleRef, labelTextRef } = useLabelTitleTooltip<HTMLSpanElement>(
    truncate,
    element.body
  )
  const displayedBody = truncate
    ? collapseNewlines(element.body)
    : element.body

  return (
    <StyledLabelHelpWrapper className="stText" data-testid="stText">
      <StyledText $truncate={truncate}>
        {truncate ? (
          <StyledTextBody ref={titleRef}>
            <span ref={labelTextRef} style={{ display: "contents" }}>
              {displayedBody}
            </span>
          </StyledTextBody>
        ) : (
          displayedBody
        )}
        {element.help && (
          <StyledInlineHelpIcon>
            <InlineTooltipIcon content={element.help} />
          </StyledInlineHelpIcon>
        )}
      </StyledText>
    </StyledLabelHelpWrapper>
  )
}

export default memo(TextElement)
